// DealComments — CRM-лента комментариев к сделке (R19.2, FR-D9).
// Хронологический порядок (новые снизу, как в чатах) — backend отдаёт ASC по createdAt.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { dealCommentsApi } from '@/api/deals';
import { useUserMap, resolveUserName } from '@/api/users';
import { formatDate } from '@/lib/format';

/** Размер страницы ленты комментариев. */
const COMMENTS_PAGE_SIZE = 50;

export interface DealCommentsProps {
  /** UUID сделки, к которой привязана лента. */
  dealId: string;
}

/** Лента комментариев сделки: список (автор · дата · текст) + композер внизу. */
export function DealComments({ dealId }: DealCommentsProps): JSX.Element {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');

  const commentsQuery = useQuery({
    queryKey: ['deal-comments', dealId],
    queryFn: () => dealCommentsApi.list(dealId, 0, COMMENTS_PAGE_SIZE),
    enabled: Boolean(dealId),
  });

  // R24 / D22: резолв авторов по ids видимых комментариев (R23 useUserMap),
  // а не legacy first-100 — автор за пределами первой сотни не теряется.
  const authorIds = commentsQuery.data?.content.map((c) => c.authorUserId) ?? [];
  const userMap = useUserMap(authorIds);

  const addMutation = useMutation({
    mutationFn: (text: string) => dealCommentsApi.add(dealId, text),
    onSuccess: () => {
      setDraft('');
      void queryClient.invalidateQueries({ queryKey: ['deal-comments', dealId] });
    },
  });

  const canSend = draft.trim().length > 0 && !addMutation.isPending;

  return (
    <Stack spacing={2} data-testid="deal-comments">
      {addMutation.isError ? (
        <Alert severity="error">
          {addMutation.error instanceof Error ? addMutation.error.message : 'Не удалось отправить комментарий'}
        </Alert>
      ) : null}
      {commentsQuery.isLoading ? (
        <LoadingSpinner />
      ) : !commentsQuery.data || commentsQuery.data.content.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Комментариев пока нет
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {commentsQuery.data.content.map((comment) => (
            <Box key={comment.id}>
              <Typography variant="caption" color="text.secondary">
                {resolveUserName(userMap, comment.authorUserId)} · {formatDate(comment.createdAt)}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {comment.body}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          multiline
          minRows={2}
          fullWidth
          placeholder="Написать комментарий…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          inputProps={{ 'aria-label': 'Новый комментарий' }}
        />
        <Button variant="contained" onClick={() => addMutation.mutate(draft.trim())} disabled={!canSend}>
          Отправить
        </Button>
      </Stack>
    </Stack>
  );
}
